import { useEffect, useLayoutEffect, useRef, useSyncExternalStore } from 'react'
import { VolumeHigh } from 'iconsax-react'
import { audioPlayer } from './audioPlayer'
import { audioStore } from './audioStore'
import type { Message } from './useChatSession'

// AI 气泡右下角的小钮:空闲=扬声器(点重听);播放中=停止方块(点中断),与聊天"打断"键同一个停止符号。
function PlayClip({ clipKey }: { clipKey: string }) {
    const playingKey = useSyncExternalStore(audioPlayer.subscribe, audioPlayer.getPlayingKey)
    const playing = playingKey === clipKey
    return (
        <button
            onClick={() => audioPlayer.toggle(clipKey)}
            aria-label={playing ? '停止' : '播放'}
            className={`glass absolute -bottom-2 -right-2 flex h-6 w-6 items-center justify-center rounded-full bg-bg-secondary shadow-sm transition active:scale-90 ${
                playing ? 'text-accent' : 'text-text-secondary'
            }`}
        >
            {playing ? (
                <span className="h-[9px] w-[9px] rounded-[2px] bg-current" />
            ) : (
                <VolumeHigh size={15} color="currentColor" variant="Linear" />
            )}
        </button>
    )
}

export function MessageList({
    messages,
    liveTranscript,
    isReplying,
    fontSize,
    blurAi,
    hasMore,
    loadingMore,
    onLoadMore
}: {
    messages: Message[]
    liveTranscript: string
    isReplying: boolean
    fontSize: number
    blurAi: boolean
    hasMore: boolean
    loadingMore: boolean
    onLoadMore: () => void
}) {
    const listRef = useRef<HTMLDivElement | null>(null)
    const prevFirstId = useRef<number | undefined>(undefined)
    const anchor = useRef<{ h: number; top: number } | null>(null) // 触发翻页时记下的滚动锚点
    const audioKeys = useSyncExternalStore(audioStore.subscribe, audioStore.getKeys) // 哪些气泡有本地音频

    // 贴底 vs 保持位置:前插了更早历史(首条 id 变、且有锚点)→ 把新增高度补到 scrollTop 不跳;否则贴底。
    // useLayoutEffect:在浏览器绘制前调整,避免闪一下。
    useLayoutEffect(() => {
        const el = listRef.current
        if (!el) return
        const firstId = messages[0]?.id
        if (anchor.current && firstId !== prevFirstId.current) {
            el.scrollTop = anchor.current.top + (el.scrollHeight - anchor.current.h)
            anchor.current = null
        } else {
            el.scrollTop = el.scrollHeight
        }
        prevFirstId.current = firstId
    }, [messages, liveTranscript])

    // 翻页结束(成功已在上面用掉锚点;失败/无更多)兜底清锚点,放行下次触发
    useEffect(() => {
        if (!loadingMore) anchor.current = null
    }, [loadingMore])

    const onScroll = () => {
        const el = listRef.current
        if (!el || !hasMore || loadingMore || anchor.current) return
        if (el.scrollTop <= 48) {
            anchor.current = { h: el.scrollHeight, top: el.scrollTop }
            onLoadMore()
        }
    }

    return (
        <div ref={listRef} onScroll={onScroll} className="no-scrollbar flex-1 overflow-y-auto px-4 py-4">
            {messages.map((m) => {
                const isUser = m.role === 'user'
                // 音频按真实会话 id 落库;AI 消息 id = _id*2+1 → floor(/2) = 会话 id。有本地音频才挂播放钮。
                const clipKey = !isUser && m.content ? String(Math.floor(m.id / 2)) : null
                return (
                    <div key={m.id} className={`mb-3.5 flex ${isUser ? 'justify-end' : 'justify-start'}`}>
                        {/* 外层 relative 包一层:播放按钮做兄弟节点放在模糊层之外,糊文字时按钮仍清晰可点 */}
                        <div className="relative max-w-[78%]">
                            <div
                                style={{ fontSize }}
                                // 模糊开关:糊整个 AI 气泡(连边框/背景一起),不只糊文字。transition 常驻才能平滑过渡
                                className={`break-words rounded-3xl px-4 py-2.5 leading-relaxed transition duration-300 ${
                                    isUser
                                        ? 'liquid rounded-br-md text-white shadow-md shadow-accent/25'
                                        : `glass rounded-bl-md bg-bubble-ai text-text-primary${
                                              blurAi ? ' select-none opacity-35 blur-[2.5px]' : ''
                                          }`
                                }`}
                            >
                                {m.content || (!isUser && isReplying ? '…' : '')}
                            </div>
                            {clipKey && audioKeys.has(clipKey) && <PlayClip clipKey={clipKey} />}
                        </div>
                    </div>
                )
            })}
            {/* 语音实时 partial:暂定用户气泡 */}
            {liveTranscript && (
                <div className="mb-3.5 flex justify-end">
                    <div
                        style={{ fontSize }}
                        className="liquid max-w-[78%] break-words rounded-3xl rounded-br-md px-4 py-2.5 leading-relaxed text-white opacity-70"
                    >
                        {liveTranscript}
                    </div>
                </div>
            )}
        </div>
    )
}
